import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ResidentialHeader, useResidentialBasePath } from '../ResidentialHome';

/**
 * Phase 5.D — gate settings.
 *
 * The first field is editable today: gate name (PATCH /api/residential
 * /gates/:id). Notifications, weekly lock schedule, family-access scope
 * and event history land on later sub-phases (5.E/5.F polish + future
 * push-notification work); the section cards stay in place as visible
 * "coming soon" placeholders so the layout doesn't shift around when
 * they wire up.
 */
function ResidentialGateSettings() {
  const { gateId } = useParams();
  const basePath = useResidentialBasePath();
  const isPreview = basePath !== '/r';
  const [gate, setGate] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isPreview) {
      setGate({
        id: parseInt(gateId, 10),
        name: 'שער חצר',
        is_open: false,
        is_locked: false,
        detail: {
          product_name: 'Rolmatic תריס נגלל',
          product_manufacturer: 'HORMANN',
          product_model_number: 'RM-PRO',
          installed_at: '2026-05-22T08:30:00+03:00',
          cycle_count: 47,
          control_connected: true,
          lock_connected: false,
        },
      });
      setName('שער חצר');
      return;
    }
    axios.get(`/api/residential/gates/${gateId}`)
      .then(res => { setGate(res.data); setName(res.data.name); })
      .catch(err => setLoadError(err.response?.data?.error || 'שגיאה בטעינת השער'));
  }, [gateId, isPreview]);

  const saveName = async (e) => {
    e.preventDefault();
    setSaveError('');
    if (!name.trim()) {
      setSaveError('שם השער לא יכול להיות ריק');
      return;
    }
    if (isPreview) {
      setGate(g => ({ ...g, name: name.trim() }));
      setEditingName(false);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`/api/residential/gates/${gateId}`, { name: name.trim() });
      setGate(res.data);
      setEditingName(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ResidentialHeader title="הגדרות שער" backTo={`${basePath}/`} />
      <div className="r-subpage">
        {loadError && (
          <div className="r-banner-stub" style={{ background: '#FEE2E2', borderColor: '#FCA5A5', color: '#991B1B' }}>
            {loadError}
          </div>
        )}

        {gate && (
          <>
            {/* Name section — the only editable field today */}
            <div className="r-subpage-section">
              <h4>שם השער</h4>
              {editingName ? (
                <form onSubmit={saveName} style={{ marginTop: '0.35rem' }}>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.8rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      color: '#1B3A5C',
                      direction: 'rtl',
                    }}
                  />
                  {saveError && (
                    <div style={{ color: '#991B1B', fontSize: '0.82rem', marginTop: '0.35rem' }}>
                      {saveError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                    <button type="submit" className="r-btn r-btn-primary" disabled={saving}>
                      {saving ? 'שומר…' : 'שמירה'}
                    </button>
                    <button
                      type="button"
                      className="r-btn r-btn-secondary"
                      onClick={() => { setEditingName(false); setName(gate.name); setSaveError(''); }}
                    >
                      ביטול
                    </button>
                  </div>
                </form>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="value">{gate.name}</div>
                  <button
                    type="button"
                    onClick={() => setEditingName(true)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#F97316',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    עריכה
                  </button>
                </div>
              )}
            </div>

            <GateBasicsSection detail={gate.detail} />

            <ComingSoonSection
              title="התראות"
              text="פופ-אפ בלבד כשהשער נפתח. התראות push לנייד בפיתוח."
            />

            <ComingSoonSection
              title="לוח זמני נעילה"
              text="נעילה אוטומטית לפי שעות וימים — תופעל ברגע ששער יחובר לרכיב i1."
            />

            <ComingSoonSection
              title="בני בית עם גישה"
              text="ניהול בני המשפחה זמין מתפריט הפרופיל."
            />

            <ComingSoonSection
              title="היסטוריית פתיחות"
              text="רישום אחרונים יוצג כאן ברגע ששער יתחיל לפעול בפועל."
            />
          </>
        )}
      </div>
    </>
  );
}

function ComingSoonSection({ title, text }) {
  return (
    <div className="r-subpage-section" style={{ opacity: 0.85 }}>
      <h4>{title}</h4>
      <div className="value" style={{ fontWeight: 400, color: '#64748b', fontSize: '0.92rem' }}>
        {text}
      </div>
    </div>
  );
}

/**
 * Phase 5 — "פרטי השער" card. Shows the basics the resident actually
 * cares about (model, install date, cycle count) plus a clear
 * connected / not-connected indicator for each of the paired
 * Shellys. No identifiers, no MQTT topics, no install_status —
 * those stay admin-side.
 */
function GateBasicsSection({ detail }) {
  if (!detail) return null;
  const installed = detail.installed_at
    ? new Date(detail.installed_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' })
    : null;
  const productLabel = detail.product_name
    ? detail.product_manufacturer
      ? `${detail.product_name} · ${detail.product_manufacturer}`
      : detail.product_name
    : null;
  const Pill = ({ ok, label }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      fontSize: '0.78rem', fontWeight: 600,
      padding: '0.2rem 0.65rem', borderRadius: '999px',
      background: ok ? '#DCFCE7' : '#FEF3C7',
      color: ok ? '#15803D' : '#92400E',
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: ok ? '#22C55E' : '#F59E0B',
      }} />
      {label}
    </span>
  );
  return (
    <div className="r-subpage-section">
      <h4>פרטי השער</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
        <Row label="דגם" value={productLabel || 'לא מוגדר'} />
        <Row label="תאריך התקנה" value={installed || 'טרם הותקן'} />
        <Row label="ספירת פתיחות" value={String(detail.cycle_count ?? 0)} />
        <Row
          label="רכיב פתיחה"
          value={<Pill ok={detail.control_connected} label={detail.control_connected ? 'מחובר' : 'לא מחובר'} />}
        />
        <Row
          label="רכיב נעילה"
          value={<Pill ok={detail.lock_connected} label={detail.lock_connected ? 'מחובר' : 'לא מחובר'} />}
        />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>{label}</span>
      <span style={{ fontWeight: 500, color: '#1B3A5C', fontSize: '0.95rem' }}>{value}</span>
    </div>
  );
}

export default ResidentialGateSettings;
